/**
 * Protected Route Component
 * Redirects to login if not authenticated, optionally checks for admin role
 */

import { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../lib/constants';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  loginPath?: string;
}

export default function ProtectedRoute({ children, requireAdmin = false, loginPath = ROUTES.STAFF_LOGIN }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isAdminOrManager } = useAuth();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  // Authenticated but not admin/manager when admin is required
  if (requireAdmin && !isAdminOrManager()) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-6">管理者またはマネージャー権限が必要です。</p>
          <a href={ROUTES.HOME} className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors">
            Go Home
          </a>
        </div>
      </div>
    );
  }

  // Authenticated (and admin if required) - show protected content
  return <>{children}</>;
}
